<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { NButton, NEmpty, NInput, NModal, NSpin } from 'naive-ui'
import AppTreeView from '@/components/AppTreeView.vue'
import type { ResourceCategoryKey } from '@/features/virtual-resource/constants'
import {
  findResourceFileMetaByStoredPath,
  normalizeResourceBaseFolderPath,
  resolveDisplayPathFromFileName,
} from '@/features/virtual-resource/fileNamePath'
import { isHiddenResourceTopFolder } from '@/features/virtual-resource/rules'
import {
  fetchProjectFontBlobFromUrl,
  resolveFontDisplayNameFromAsset,
  resolveFontDisplayNameFromStoredPath,
} from '@/features/project-font/runtime'
import { FONT_DISPLAY_NAME_LINK_INDEX, FONT_PREVIEW_MD5_LINK_INDEX } from '@/features/project-font/library'
import { buildCurrentProjectResourceUrl } from '@/features/static-resource/resourceCdn'
import {
  createSafariAudioPlaybackUrl,
  inferAudioMimeFromName,
  releaseSafariAudioObjectUrls,
} from '@/features/audio-playback/safariAudioPlayback'
import type DataFileMap from '@/model/Resource/DataFileMap'
import { useWorkspaceStore } from '@/store/workspace'
import { blurModalSearchFocus } from '@/utils/blurModalSearchFocus'

type PickerItem = {
  key: string
  label: string
  relativePath: string
  resourceUrl: string
  previewUrl: string
  folderPath: string
  node: DataFileMap
}

type FolderTreeNode = {
  key: string
  label: string
  children?: FolderTreeNode[]
}

const BASE_ROOT_KEY = '__base_root__'

const props = defineProps<{
  show: boolean
  category: ResourceCategoryKey
  title?: string
  search?: string
  selectedPath?: string
  baseFolderPath?: string
  /** 无已选路径或路径在列表中找不到时，定位到的文件夹（如 `背景`、`立绘`） */
  fallbackFolderPath?: string
}>()

const emit = defineEmits<{
  'update:show': [value: boolean]
  'update:search': [value: string]
  select: [value: string]
}>()

const workspace = useWorkspaceStore()
const selectedFolderKey = ref('')
const selectedItemPath = ref('')
const expandedFolderKeys = ref<Array<string | number>>([])
const folderPanelRef = ref<HTMLElement | null>(null)
const listRef = ref<HTMLElement | null>(null)
const fontPreviewUrl = ref('')
const pickerAudioPlaybackUrl = ref('')
const pickerAudioRef = ref<HTMLAudioElement | null>(null)
const fontSelectLoading = ref(false)
let lastPointerItemPath = ''
let lastPointerTime = 0
let fontPreviewToken = 0
const loadedPickerFontFaces = new Map<string, FontFace>()

function normalizeFolderPath(path: string): string {
  return path.replace(/^\/+|\/+$/g, '').trim()
}

function getPickerItemLabel(relativePath: string, fileName: string): string {
  if (props.category !== 'font') return fileName || relativePath
  return resolveFontDisplayNameFromStoredPath(
    workspace.virtualResourceRoots.font,
    relativePath || fileName,
    normalizedBaseFolderPath.value || undefined,
  )
}

function getPickerTypeLabel(): string {
  if (props.category === 'font') return 'TTF'
  return props.category
}

function getFontPreviewUrl(node: DataFileMap): string {
  const previewMd5 = node.links[FONT_PREVIEW_MD5_LINK_INDEX]?.trim().toLowerCase() ?? ''
  return previewMd5 ? buildCurrentProjectResourceUrl(previewMd5) : ''
}

function getFontPreviewFamily(item: PickerItem): string {
  const hash = item.relativePath
    .split('')
    .reduce((sum, char) => (sum * 31 + char.charCodeAt(0)) >>> 0, 0)
  return `avgmaker-picker-font-${hash}`
}

async function loadPickerFontFamily(item: PickerItem): Promise<string> {
  if (typeof FontFace === 'undefined' || !document.fonts || !item.resourceUrl) return 'sans-serif'
  const family = getFontPreviewFamily(item)
  if (loadedPickerFontFaces.has(family)) return family
  const fontBlob = await fetchProjectFontBlobFromUrl(item.resourceUrl)
  await enrichPickerFontMetadata(item, fontBlob)
  const fontUrl = URL.createObjectURL(fontBlob)
  try {
    const fontFace = new FontFace(family, `url(${fontUrl})`)
    await fontFace.load()
    document.fonts.add(fontFace)
    await document.fonts.load(`24px "${family}"`)
    loadedPickerFontFaces.set(family, fontFace)
  } finally {
    URL.revokeObjectURL(fontUrl)
  }
  return family
}

async function enrichPickerFontMetadata(item: PickerItem, fontBlob: Blob): Promise<void> {
  const parsedDisplayName = await resolveFontDisplayNameFromAsset(item.node.name, fontBlob)
  const cleanDisplayName = parsedDisplayName.trim()
  if (!cleanDisplayName || item.node.links[FONT_DISPLAY_NAME_LINK_INDEX] === cleanDisplayName) return
  item.node.links[FONT_DISPLAY_NAME_LINK_INDEX] = cleanDisplayName
  item.label = cleanDisplayName
  workspace.virtualResourcesDirty = true
  void workspace.persistVirtualResources({
    uploadToCloud: true,
    ignoreUploadError: true,
  })
}

function drawFontPreviewImage(item: PickerItem, family: string): string {
  const canvas = document.createElement('canvas')
  canvas.width = 520
  canvas.height = 360
  const ctx = canvas.getContext('2d')
  if (!ctx) return ''
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.fillStyle = '#1f2937'
  ctx.font = `28px "${family}", sans-serif`
  ctx.fillText(item.label, 28, 52)
  ctx.fillStyle = '#6b7280'
  ctx.font = `14px "${family}", sans-serif`
  ctx.fillText(item.relativePath, 28, 82)
  const samples = [
    '橙光轻量版 字体预览',
    '方寸之间，自有天地。',
    'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
    'abcdefghijklmnopqrstuvwxyz',
    '0123456789 。，、；：？！',
  ]
  const sizes = [30, 26, 22, 20, 20]
  samples.forEach((text, index) => {
    ctx.fillStyle = index === 0 ? '#111827' : '#374151'
    ctx.font = `${sizes[index]}px "${family}", sans-serif`
    ctx.fillText(text, 28, 135 + index * 42)
  })
  return canvas.toDataURL('image/png')
}

async function updateFontPreview(item: PickerItem | null): Promise<void> {
  const token = ++fontPreviewToken
  fontPreviewUrl.value = ''
  if (props.category !== 'font' || !item) return
  if (item.previewUrl) {
    fontPreviewUrl.value = item.previewUrl
    return
  }
  try {
    const family = await loadPickerFontFamily(item)
    if (token !== fontPreviewToken) return
    fontPreviewUrl.value = drawFontPreviewImage(item, family)
  } catch {}
}

function collectPickerItems(root: DataFileMap | null | undefined): PickerItem[] {
  if (!root) return []
  const out: PickerItem[] = []
  const stack: Array<{ node: DataFileMap; segments: string[]; folderPath: string }> = [
    { node: root, segments: [], folderPath: '' },
  ]
  while (stack.length) {
    const current = stack.pop()!
    if (current.node.isFolder) {
      for (let index = current.node.children.length - 1; index >= 0; index -= 1) {
        const child = current.node.children[index]!
        stack.push({
          node: child,
          segments: child.isFolder ? [...current.segments, child.name] : current.segments,
          folderPath: child.isFolder
            ? normalizeFolderPath([current.folderPath, child.name].filter(Boolean).join('/'))
            : current.folderPath,
        })
      }
      continue
    }
    const relativePath = normalizeFolderPath([...current.segments, current.node.name].filter(Boolean).join('/'))
    out.push({
      key: relativePath,
      label: getPickerItemLabel(relativePath, current.node.name),
      relativePath,
      resourceUrl: workspace.resourcePreviewOverrides[current.node.resourceUrl] ?? current.node.resourceUrl,
      previewUrl: getFontPreviewUrl(current.node),
      folderPath: current.folderPath,
      node: current.node,
    })
  }
  return out
}

function collectDirectPickerItems(root: DataFileMap | null | undefined, folderPath = ''): PickerItem[] {
  if (!root) return []
  const normalizedFolderPath = normalizeFolderPath(folderPath)
  return root.children
    .filter((child) => !child.isFolder)
    .map((child) => {
      const relativePath = normalizeFolderPath([normalizedFolderPath, child.name].filter(Boolean).join('/'))
      return {
        key: relativePath,
        label: getPickerItemLabel(relativePath, child.name),
        relativePath,
        resourceUrl: workspace.resourcePreviewOverrides[child.resourceUrl] ?? child.resourceUrl,
        previewUrl: getFontPreviewUrl(child),
        folderPath: normalizedFolderPath,
        node: child,
      }
    })
}

function findFolderByPath(root: DataFileMap | null | undefined, path: string): DataFileMap | null {
  if (!root) return null
  const segments = normalizeFolderPath(path).split('/').filter(Boolean)
  let current = root
  for (const segment of segments) {
    const next = current.children.find((child) => child.isFolder && child.name === segment)
    if (!next) return null
    current = next
  }
  return current
}

function buildFolderTree(
  root: DataFileMap | null | undefined,
  category: ResourceCategoryKey,
  parentPath = '',
): FolderTreeNode[] {
  if (!root) return []
  return root.children
    .filter(
      (child) =>
        child.isFolder
        && !(parentPath === '' && isHiddenResourceTopFolder(category, child.name)),
    )
    .map((child) => {
      const path = normalizeFolderPath([parentPath, child.name].filter(Boolean).join('/'))
      return {
        key: path,
        label: child.name,
        children: buildFolderTree(child, category, path),
      }
    })
}

const normalizedBaseFolderPath = computed(() => normalizeResourceBaseFolderPath(props.category, props.baseFolderPath ?? ''))
const baseFolderLabel = computed(() => {
  const normalized = normalizedBaseFolderPath.value
  if (!normalized) return ''
  const segments = normalized.split('/').filter(Boolean)
  return segments[segments.length - 1] ?? ''
})
const pickerRoot = computed(() => {
  const root = workspace.virtualResourceRoots[props.category]
  if (!root) return null
  const base = normalizedBaseFolderPath.value
  if (!base) return root
  return root.findFolderByRelativePath(base)
})
const pickerItems = computed(() => collectPickerItems(pickerRoot.value))
const baseRootItems = computed(() => collectDirectPickerItems(pickerRoot.value))
const folderTree = computed(() => {
  const children = buildFolderTree(pickerRoot.value, props.category)
  if (!normalizedBaseFolderPath.value) return children
  return [
    {
      key: BASE_ROOT_KEY,
      label: baseFolderLabel.value || normalizedBaseFolderPath.value,
      children,
    },
  ]
})
const activeFolderPath = computed(() => {
  if (selectedFolderKey.value === BASE_ROOT_KEY) return ''
  return normalizeFolderPath(selectedFolderKey.value)
})
const currentFolderItems = computed(() => {
  if (selectedFolderKey.value === BASE_ROOT_KEY) return baseRootItems.value
  const folder = findFolderByPath(pickerRoot.value, activeFolderPath.value)
  return collectDirectPickerItems(folder, activeFolderPath.value)
})
const filteredItems = computed(() => {
  const keyword = (props.search ?? '').trim().toLowerCase()
  const items = currentFolderItems.value
  if (!keyword) return items
  return items.filter((item) => {
    return item.label.toLowerCase().includes(keyword) || item.relativePath.toLowerCase().includes(keyword)
  })
})

const previewMode = computed(() => props.category === 'graphic')
const modalTitle = computed(() => props.title?.trim() || '选择素材')
/** 字体为工程必填项，字体选择弹窗不提供清空。 */
const showClearButton = computed(() => props.category !== 'font')
const modalWidth = 'min(920px, 94vw)'
const layoutHeight = '520px'
const selectedItem = computed(() => {
  const byPath = filteredItems.value.find((item) => item.relativePath === selectedItemPath.value)
  return byPath ?? filteredItems.value[0] ?? null
})

function stopPickerAudioPreview() {
  const audio = pickerAudioRef.value
  if (audio) {
    audio.pause()
    audio.removeAttribute('src')
    audio.load()
  }
  const prev = pickerAudioPlaybackUrl.value
  if (prev.startsWith('blob:')) {
    releaseSafariAudioObjectUrls([prev])
  }
  pickerAudioPlaybackUrl.value = ''
}

function closeModal() {
  if (fontSelectLoading.value) return
  stopPickerAudioPreview()
  emit('update:show', false)
}

async function selectCurrent() {
  if (!selectedItem.value) return
  if (props.category === 'font') {
    fontSelectLoading.value = true
    try {
      await loadPickerFontFamily(selectedItem.value)
    } finally {
      fontSelectLoading.value = false
    }
  }
  emit('select', selectedItem.value.relativePath)
  closeModal()
}

async function selectItem(item: PickerItem) {
  if (props.category === 'font') {
    fontSelectLoading.value = true
    try {
      await loadPickerFontFamily(item)
    } finally {
      fontSelectLoading.value = false
    }
  }
  emit('select', item.relativePath)
  closeModal()
}

function clearSelection() {
  emit('select', '')
  closeModal()
}

function handleItemDoubleClick(item: PickerItem, event: MouseEvent) {
  event.preventDefault()
  event.stopPropagation()
  selectedItemPath.value = item.relativePath
  void selectItem(item)
}

function handleItemClick(item: PickerItem, event: MouseEvent) {
  selectedItemPath.value = item.relativePath
  if (event.detail >= 2) {
    void selectItem(item)
  }
}

function handleItemPointerUp(item: PickerItem, event: PointerEvent) {
  if (event.button !== 0) return
  const now = window.performance.now()
  const isSecondTap = lastPointerItemPath === item.relativePath && now - lastPointerTime <= 500
  lastPointerItemPath = item.relativePath
  lastPointerTime = now
  if (!isSecondTap) return
  event.preventDefault()
  event.stopPropagation()
  selectedItemPath.value = item.relativePath
  void selectItem(item)
}

function onFolderSelect(keys: Array<string | number>) {
  selectedFolderKey.value = String(keys[0] ?? '')
}

function onFolderExpand(keys: Array<string | number>) {
  expandedFolderKeys.value = [...keys]
}

function buildExpandedFolderKeys(folderPath: string): Array<string | number> {
  const normalized = normalizeFolderPath(folderPath)
  const keys: Array<string | number> = []
  if (normalizedBaseFolderPath.value) {
    keys.push(BASE_ROOT_KEY)
  }
  if (!normalized) return keys
  const segments = normalized.split('/').filter(Boolean)
  let current = ''
  for (const segment of segments) {
    current = current ? `${current}/${segment}` : segment
    keys.push(current)
  }
  return keys
}

function findPickerItemBySelectedPath(selected: string): PickerItem | null {
  const normalized = normalizeFolderPath(selected)
  if (!normalized) return null
  const exact = pickerItems.value.find((item) => item.relativePath === normalized)
  if (exact) return exact
  const meta = findResourceFileMetaByStoredPath(
    workspace.virtualResourceRoots[props.category],
    props.category,
    normalized,
    props.baseFolderPath,
  )
  const byNode = meta ? pickerItems.value.find((item) => item.node === meta.node) : null
  if (byNode) return byNode
  const resolved = resolveDisplayPathFromFileName(
    workspace.virtualResourceRoots[props.category],
    props.category,
    normalized,
    normalizedBaseFolderPath.value ? 'relative' : 'mapped',
    props.baseFolderPath,
  )
  return pickerItems.value.find((item) => item.relativePath === resolved) ?? null
}

async function scrollSelectedItemIntoView() {
  await nextTick()
  const listEl = listRef.value
  const selected = selectedItemPath.value
  if (!listEl || !selected) return
  const target = listEl.querySelector<HTMLElement>(`.resource-picker-item[data-path="${CSS.escape(selected)}"]`)
  target?.scrollIntoView({ block: 'nearest' })
}

async function scrollSelectedFolderIntoView() {
  await nextTick()
  const folderEl = folderPanelRef.value
  const selected = selectedFolderKey.value
  if (!folderEl || !selected) return
  const target = folderEl.querySelector<HTMLElement>(`[data-folder-key="${CSS.escape(selected)}"]`)
  target?.scrollIntoView({ block: 'nearest' })
}

function scrollSelectionIntoView() {
  void scrollSelectedFolderIntoView()
  void scrollSelectedItemIntoView()
}

function applySelectedItem(item: PickerItem | null): void {
  if (!item) {
    selectedFolderKey.value = normalizedBaseFolderPath.value ? BASE_ROOT_KEY : ''
    selectedItemPath.value = ''
    expandedFolderKeys.value = buildExpandedFolderKeys('')
    return
  }
  selectedFolderKey.value = item.folderPath || (normalizedBaseFolderPath.value ? BASE_ROOT_KEY : '')
  selectedItemPath.value = item.relativePath
  expandedFolderKeys.value = buildExpandedFolderKeys(item.folderPath)
}

function applyFallbackFolder(folderPath: string): void {
  const normalized = normalizeResourceBaseFolderPath(props.category, folderPath)
  selectedFolderKey.value = normalized || (normalizedBaseFolderPath.value ? BASE_ROOT_KEY : '')
  selectedItemPath.value = ''
  expandedFolderKeys.value = buildExpandedFolderKeys(normalized)
}

function ensureSelection() {
  const selected = (props.selectedPath ?? '').trim()
  const hit = selected ? findPickerItemBySelectedPath(selected) : null
  if (hit) {
    applySelectedItem(hit)
    return
  }
  const fallback = normalizeResourceBaseFolderPath(props.category, props.fallbackFolderPath ?? '')
  if (fallback) {
    applyFallbackFolder(props.fallbackFolderPath ?? '')
    return
  }
  applySelectedItem(pickerItems.value[0] ?? null)
}

function folderTreeNodeProps(info: { option: { key?: string | number | null } }) {
  return {
    'data-folder-key': String(info.option.key ?? ''),
  }
}

watch(
  () => props.show,
  (show) => {
    if (!show) {
      stopPickerAudioPreview()
      return
    }
    selectedFolderKey.value = ''
    selectedItemPath.value = ''
    expandedFolderKeys.value = []
    ensureSelection()
    scrollSelectionIntoView()
  },
  { immediate: true },
)

watch(folderTree, () => {
  ensureSelection()
  scrollSelectionIntoView()
})

watch(filteredItems, (items) => {
  const exists = items.some((item) => item.relativePath === selectedItemPath.value)
  if (!exists) {
    selectedItemPath.value = items[0]?.relativePath ?? ''
  }
  scrollSelectionIntoView()
})

watch(
  () => props.selectedPath,
  () => {
    if (!props.show) return
    ensureSelection()
    scrollSelectionIntoView()
  },
)

watch(
  () => selectedItem.value,
  (item) => {
    void updateFontPreview(item)
    void updatePickerAudioPreview(item)
  },
  { immediate: true },
)

async function updatePickerAudioPreview(item: PickerItem | null | undefined): Promise<void> {
  stopPickerAudioPreview()
  if (props.category !== 'audio' || !item?.resourceUrl) {
    return
  }
  try {
    pickerAudioPlaybackUrl.value = await createSafariAudioPlaybackUrl(
      item.resourceUrl,
      inferAudioMimeFromName(item.relativePath || item.label),
    )
  } catch {
    pickerAudioPlaybackUrl.value = item.resourceUrl
  }
}

onBeforeUnmount(() => {
  stopPickerAudioPreview()
})
</script>

<template>
  <n-modal
    :show="show"
    preset="card"
    :title="modalTitle"
    class="resource-picker-modal"
    :mask-closable="true"
    :auto-focus="false"
    :style="{ width: modalWidth }"
    @after-enter="blurModalSearchFocus('.resource-picker-modal')"
    @update:show="emit('update:show', $event)"
  >
    <div class="resource-picker-shell">
      <n-input
        class="resource-picker-search resource-picker-search--desktop"
        :value="search ?? ''"
        size="small"
        clearable
        :autofocus="false"
        placeholder="搜索素材路径"
        @update:value="emit('update:search', $event)"
      />

      <div class="resource-picker-layout" :style="{ height: layoutHeight, maxHeight: '70vh' }">
        <section ref="folderPanelRef" class="resource-picker-panel resource-picker-panel--tree">
          <div class="resource-picker-caption">目录</div>
          <AppTreeView
            v-if="folderTree.length"
            class="resource-picker-tree"
            :data="folderTree"
            :expanded-keys="expandedFolderKeys"
            :selected-keys="selectedFolderKey ? [selectedFolderKey] : []"
            :node-props="folderTreeNodeProps"
            @update:selected-keys="onFolderSelect"
            @update:expanded-keys="onFolderExpand"
          />
          <n-empty
            v-else
            class="resource-picker-empty"
            :description="normalizedBaseFolderPath && !pickerRoot ? '指定目录不存在' : '暂无目录'"
            size="small"
          />
        </section>

        <section class="resource-picker-panel resource-picker-panel--list">
          <div class="resource-picker-caption">素材</div>
          <n-input
            class="resource-picker-search resource-picker-search--portrait"
            :value="search ?? ''"
            size="small"
            clearable
            :autofocus="false"
            placeholder="搜索素材路径"
            @update:value="emit('update:search', $event)"
          />
          <div v-if="filteredItems.length" ref="listRef" class="resource-picker-list">
            <button
              v-for="item in filteredItems"
              :key="item.key"
              type="button"
              class="resource-picker-item"
              :data-path="item.relativePath"
              :class="{ 'resource-picker-item--active': selectedItemPath === item.relativePath }"
              @pointerup="handleItemPointerUp(item, $event)"
              @click="handleItemClick(item, $event)"
              @dblclick="handleItemDoubleClick(item, $event)"
            >
              <div class="resource-picker-preview">
                <img
                  v-if="previewMode && item.resourceUrl"
                  :src="item.resourceUrl"
                  :alt="item.label"
                  draggable="false"
                />
                <span v-else-if="category === 'font'" class="resource-picker-font-icon" aria-hidden="true">Aa</span>
                <svg
                  v-else-if="category === 'audio'"
                  class="resource-picker-audio-icon"
                  viewBox="0 0 48 48"
                  width="42"
                  height="42"
                  aria-hidden="true"
                >
                  <path d="M31 8v23.5a7.5 7.5 0 1 1-4-6.62V14.6l-12 2.78V36.5a7.5 7.5 0 1 1-4-6.62V15.8L31 11.1Z" />
                  <path d="M36.2 18.4c2.5 1.8 4 4.4 4 7.6s-1.5 5.8-4 7.6" />
                </svg>
                <span v-else class="resource-picker-type">{{ getPickerTypeLabel() }}</span>
              </div>
              <div class="resource-picker-meta">
                <div class="resource-picker-name">{{ item.label }}</div>
              </div>
            </button>
          </div>
          <n-empty v-else description="当前目录没有素材" size="small" />
        </section>

        <section class="resource-picker-panel resource-picker-panel--preview">
          <div class="resource-picker-caption">预览</div>
          <div v-if="selectedItem" class="resource-picker-preview-pane">
            <img
              v-if="category === 'graphic' && selectedItem.resourceUrl"
              class="resource-picker-preview-image"
              :src="selectedItem.resourceUrl"
              :alt="selectedItem.label"
              draggable="false"
            />
            <audio
              v-else-if="category === 'audio' && pickerAudioPlaybackUrl"
              ref="pickerAudioRef"
              class="resource-picker-preview-audio"
              :src="pickerAudioPlaybackUrl"
              controls
            />
            <video
              v-else-if="category === 'video' && selectedItem.resourceUrl"
              class="resource-picker-preview-video"
              :src="selectedItem.resourceUrl"
              controls
            />
            <img
              v-else-if="category === 'font' && fontPreviewUrl"
              class="resource-picker-preview-image"
              :src="fontPreviewUrl"
              :alt="selectedItem.label"
              draggable="false"
            />
            <div v-else class="resource-picker-preview-text">
              {{ category === 'font' ? selectedItem.label : selectedItem.relativePath }}
            </div>
          </div>
          <n-empty v-else description="请选择素材" size="small" />
        </section>
      </div>

      <div class="resource-picker-actions">
        <n-button v-if="showClearButton" size="small" @click="clearSelection">清空资源</n-button>
        <n-button size="small" @click="closeModal">取消</n-button>
        <n-button
          size="small"
          type="primary"
          :disabled="!selectedItem"
          :loading="fontSelectLoading"
          @click="selectCurrent"
        >
          {{ fontSelectLoading ? '字体加载中…' : '确定' }}
        </n-button>
      </div>
      <n-spin v-if="fontSelectLoading" :show="true" class="resource-picker-loading">
        <div class="resource-picker-loading-text">字体加载中…</div>
      </n-spin>
    </div>
  </n-modal>
</template>

<style scoped>
:global(.resource-picker-modal.n-modal) {
  max-width: 96vw;
}

.resource-picker-search--portrait {
  display: none;
}

.resource-picker-shell {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 0;
}

.resource-picker-layout {
  display: grid;
  grid-template-columns: minmax(180px, 220px) minmax(240px, 1fr) minmax(220px, 280px);
  gap: 10px;
  min-height: 0;
}

.resource-picker-panel {
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-card);
}

.resource-picker-caption {
  padding: 8px 10px;
  border-bottom: 1px solid var(--theme-border);
  font-size: 12px;
  color: var(--theme-text-secondary);
}

.resource-picker-tree {
  flex: 1;
  min-height: 0;
}

.resource-picker-empty {
  margin: auto;
}

.resource-picker-list {
  flex: 1;
  min-height: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
}

.resource-picker-item {
  display: grid;
  grid-template-columns: 60px minmax(0, 1fr);
  gap: 8px;
  padding: 8px;
  border: 0;
  border-bottom: 1px solid var(--theme-border);
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.resource-picker-item--active {
  background: var(--theme-bg-selected, var(--theme-bg-card));
}

.resource-picker-preview {
  width: 60px;
  height: 60px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-raised);
}

.resource-picker-preview img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.resource-picker-type {
  font-size: 11px;
  color: var(--theme-text-muted);
}

.resource-picker-font-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 42px;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-card);
  color: var(--theme-text-heading);
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
}

.resource-picker-audio-icon {
  display: block;
  flex: 0 0 auto;
  width: 42px;
  height: 42px;
  color: var(--theme-accent);
  fill: currentColor;
}

.resource-picker-audio-icon path:last-child {
  fill: none;
  stroke: currentColor;
  stroke-width: 4;
  stroke-linecap: round;
}

.resource-picker-meta {
  min-width: 0;
}

.resource-picker-name {
  font-size: 12px;
  color: var(--theme-text-heading);
  word-break: break-all;
}

.resource-picker-preview-pane {
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
}

.resource-picker-preview-image,
.resource-picker-preview-video {
  display: block;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.resource-picker-preview-audio {
  width: 100%;
}

.resource-picker-preview-text {
  font-size: 12px;
  color: var(--theme-text-secondary);
  word-break: break-all;
}

.resource-picker-loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--theme-bg-surface);
  z-index: 2;
}

.resource-picker-loading-text {
  padding: 8px 12px;
  color: var(--theme-text-heading);
  background: var(--theme-bg-card);
}

.resource-picker-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

@media (max-width: 1180px) and (orientation: portrait) {
  :global(.resource-picker-modal.n-modal) {
    width: 100vw !important;
    max-width: 100vw;
    height: 100vh;
    max-height: 100vh;
  }

  :global(.resource-picker-modal.n-modal .n-card__content) {
    min-height: 0;
    padding: 0 !important;
    box-sizing: border-box;
  }

  .resource-picker-shell {
    display: grid;
    grid-template-rows: minmax(0, 1fr) 38px;
    gap: 0;
    height: calc(100vh - 47px);
    min-height: 0;
    background: var(--theme-bg-panel);
  }

  .resource-picker-search--desktop {
    display: none;
  }

  .resource-picker-layout {
    display: grid;
    grid-template-columns: minmax(132px, 34%) minmax(0, 1fr);
    grid-template-rows: minmax(150px, min(42%, calc(100vw * 0.5625))) minmax(0, 1fr);
    grid-template-areas:
      "preview preview"
      "tree list";
    gap: 0;
    height: auto !important;
    max-height: none !important;
    min-height: 0;
    overflow: hidden;
  }

  .resource-picker-panel {
    border: 0;
    border-bottom: 1px solid var(--theme-border);
  }

  .resource-picker-panel--preview {
    grid-area: preview;
  }

  .resource-picker-panel--tree {
    grid-area: tree;
    border-right: 1px solid var(--theme-border);
  }

  .resource-picker-panel--list {
    grid-area: list;
  }

  .resource-picker-search--portrait {
    display: block;
    flex: 0 0 auto;
    padding: 6px;
    border-bottom: 1px solid var(--theme-border);
    box-sizing: border-box;
  }

  .resource-picker-caption {
    padding: 6px 8px;
  }

  .resource-picker-preview-pane {
    padding: 6px;
  }

  .resource-picker-item {
    grid-template-columns: 44px minmax(0, 1fr);
    gap: 6px;
    padding: 6px;
  }

  .resource-picker-preview {
    width: 44px;
    height: 44px;
  }

  .resource-picker-actions {
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding: 4px 8px;
    border-top: 1px solid var(--theme-border);
    background: var(--theme-bg-panel);
    box-sizing: border-box;
  }
}
</style>
